import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { BaseCampaign, Recommendation, TTDCampaign } from '@feathr/blackbox';
import { FilterCarousel } from '@feathr/components';
import CampaignPage from '@feathr/extender/App/EventsPage/CampaignPage';
import ReportActions from '@feathr/extender/components/PageHeaderActions';
import { useReportExport } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';

import CampaignReportSection, { getCampaignReportConfig } from '../CampaignReportSection';
import CampaignRecommendations from './CampaignRecommendations';
import {
  channelFilterToStatsKey,
  EChannelFilter,
  useChannelFilterTabs,
} from './DefaultCampaignPage.hooks';
import useRecommendations from './DefaultCampaignPage.useRecommendations';
import GoogleAdsQuotaToast from './GoogleAdsQuotaToast';

import * as styles from './DefaultCampaignPage.css';

/**
 * Default campaign report page component used for Tradedesk, Facebook and Google campaigns: Affinity, Email Mapping, Lookalike, Historical Geofencing, Mobile Geofencing, Retargeting, Search, Facebook Email Mapping, Facebook Retargeting and Google Ads Grants
 *
 * @returns The default campaign page component
 */
function DefaultCampaignPage(): JSX.Element {
  const { t } = useTranslation();
  const { Campaigns } = useStore();
  const { campaignId } = useParams<{ eventId: string; campaignId: string }>();

  const campaign: BaseCampaign = Campaigns.get(campaignId);
  const config = getCampaignReportConfig(campaign);
  const reportExportProps = useReportExport({ model: campaign, config });
  const {
    recommendations,
    hasActiveRecommendationsOnLoad,
    hasRecommendationsDismissed,
    onMarkRecommendationDismissed,
    showRecommendations,
    onRefreshCollection,
    onDismissRefreshCollection,
  } = useRecommendations({
    campaign,
  });

  const { availableChannels, shouldShowChannelTabs } = useChannelFilterTabs(campaign);
  const [activeChannel, setActiveChannel] = useState<EChannelFilter>(EChannelFilter.Overview);
  const hideReachBreakdown = activeChannel !== EChannelFilter.Overview;

  const channelLabels: Record<EChannelFilter, string> = {
    [EChannelFilter.Overview]: t('Overview'),
    [EChannelFilter.Display]: t('Display'),
    [EChannelFilter.Meta]: t('Meta'),
  };

  const carouselItems = availableChannels.map((channel) => ({
    content: channelLabels[channel],
    onClick: () => setActiveChannel(channel),
  }));

  const carousel = shouldShowChannelTabs ? (
    <div className={styles.carousel}>
      <FilterCarousel
        initialSlide={'prefix'}
        items={carouselItems}
        prefix={{
          content: t('Overview'),
          onClick: () => setActiveChannel(EChannelFilter.Overview),
        }}
      />
    </div>
  ) : undefined;

  const actions = <ReportActions campaign={campaign} {...reportExportProps} />;

  return (
    <CampaignPage
      actions={actions}
      campaign={campaign}
      className={styles.root}
      headerChildren={carousel}
      title={campaign.name}
    >
      {campaign.isGoogle && <GoogleAdsQuotaToast />}
      {showRecommendations && (
        <CampaignRecommendations
          campaign={campaign as TTDCampaign}
          hasActiveRecommendationsOnLoad={hasActiveRecommendationsOnLoad}
          hasRecommendationsDismissed={hasRecommendationsDismissed}
          onDismissRefreshCollection={onDismissRefreshCollection}
          onMarkRecommendationDismissed={onMarkRecommendationDismissed}
          onRefreshCollection={onRefreshCollection}
          recommendations={recommendations?.models as IObservableArray<Recommendation>}
        />
      )}
      <h3>{t('Report')}</h3>
      <CampaignReportSection
        activeChannel={channelFilterToStatsKey[activeChannel]}
        campaign={campaign}
        hideReachBreakdown={hideReachBreakdown}
        {...reportExportProps}
      />
    </CampaignPage>
  );
}

export default observer(DefaultCampaignPage);
